import {
  type CreateQualificationFields,
  type External,
  isRequiredCoding,
  type PractitionerQualificationCode,
  type QualificationFields,
  type Settings,
} from '@quromedical/models';
import { first } from '@quromedical/utils';
import { CrudForm, type SubmissionResult } from 'components/form';
import { mapCodingToSelectOption } from 'core/display';
import type React from 'react';
import { strings } from 'strings';

import { createExternalFields } from './PractitionerExternalForm';
import { createQualificationFields } from './PractitionerQualificationForm';
import { settingsFields } from './PractitionerSettingsForm';

interface QualificationFormProps {
  data?: Partial<QualificationFields>;
  handleSubmit: (
    data: CreateQualificationFields,
  ) => Promise<SubmissionResult<unknown>>;
  handleCancel: () => void;
}

interface ExternalFormProps {
  data?: Partial<External>;
  handleSubmit: (data: External) => Promise<SubmissionResult<unknown>>;
  handleCancel: () => void;
}

interface SettingsFormProps {
  data?: Partial<Settings>;
  handleSubmit: (data: Settings) => Promise<SubmissionResult<unknown>>;
  handleCancel: () => void;
}

const getCodingFromQualificationCode = (
  qualificationCode?: PractitionerQualificationCode,
) => first(qualificationCode?.code.coding);

export const QualificationForm: React.FC<QualificationFormProps> = ({
  data = {},
  handleCancel,
  handleSubmit,
}) => {
  const { qualificationCode } = data;
  const coding = getCodingFromQualificationCode(qualificationCode);
  const selectedQualificationOption = isRequiredCoding(coding)
    ? mapCodingToSelectOption('display | code', coding)
    : undefined;

  const initialData: Partial<CreateQualificationFields> = {
    registrationNumber: data.registrationNumber,
    qualificationCode: coding?.code,
  };

  const fields = createQualificationFields(selectedQualificationOption);

  return (
    <CrudForm
      title={strings.FormLabelPractitionerQualificationDetails}
      fields={fields}
      initialValues={initialData}
      onSubmit={handleSubmit}
      onSecondarySubmit={handleCancel}
      showSecondarySubmitButton
      showSubmitButton
    />
  );
};

export const ExternalForm: React.FC<ExternalFormProps> = ({
  data = {},
  handleCancel,
  handleSubmit,
}) => {
  const initialData: Partial<External> = {
    ampathMnemonic: data.ampathMnemonic,
  };

  const fields = createExternalFields();

  return (
    <CrudForm
      title={strings.FormLabelPractitionerExternal}
      fields={fields}
      initialValues={initialData}
      onSubmit={handleSubmit}
      onSecondarySubmit={handleCancel}
      showSecondarySubmitButton
      showSubmitButton
    />
  );
};

export const SettingsForm: React.FC<SettingsFormProps> = ({
  data = {},
  handleCancel,
  handleSubmit,
}) => {
  const { canPrescribe } = data;

  const initialData: Partial<Settings> = {
    canPrescribe,
  };

  const fields = settingsFields();

  return (
    <CrudForm
      title={strings.FormLabelPractitionerCanPrescribeSettings}
      fields={fields}
      initialValues={initialData}
      onSubmit={handleSubmit}
      onSecondarySubmit={handleCancel}
      showSecondarySubmitButton
      showSubmitButton
    />
  );
};
